import type { AppState } from '../state/store';
import { OBOL_CONTRACT_ADDRESS } from '../api/obolToken';
import { versionedAssetUrl } from '../assets/version';
import { busyBar, errorBanner, esc, noticeBanner } from './components';
import { headerActionButtons } from './headerActions';

const WAYSTATION_STAGE_SRC = '/assets/sprites/hub/hub_waystation_scene_01.png';

// Production-safe placeholders: there is no public homepage telemetry source for
// online or monthly players yet, and the authenticated hub presence endpoint is
// not a public monthly-player stat.
const ONLINE_PLAYERS_STATUS = '—';
const MONTHLY_PLAYERS_STATUS = 'Tracking soon';

export function renderLanding(state: AppState): string {
  return `
    <div class="home-page" data-home-page>
      <a class="home-skip" href="#home-main">Skip to home content</a>
      ${WaystationStageBackground()}
      ${HomeHeader()}
      <main id="home-main" class="home-main">
        <div class="home-alerts">
          ${busyBar(state.busy)}
          ${errorBanner(state.error)}
          ${noticeBanner(state.notice)}
        </div>
        ${HomeHero()}
      </main>
    </div>`;
}

function WaystationStageBackground(): string {
  return `
    <div class="home-stage" aria-hidden="true">
      <img class="home-stage__art" src="${versionedAssetUrl(WAYSTATION_STAGE_SRC)}" alt="" loading="eager" decoding="async">
    </div>`;
}

function HomeHeader(): string {
  return `
    <header class="home-header" aria-label="Gravelight homepage">
      <div class="home-header__left">
        <a class="home-brand" href="/" data-act="goto-landing" aria-label="Gravelight homepage">
          <span class="home-brand__mark" aria-hidden="true"></span>
          <span>Gravelight</span>
        </a>
        <nav class="home-nav" aria-label="Home help navigation">
          <a class="home-nav__link" href="/how-to-play" data-act="goto-how-to-play">How To Play</a>
          <a class="home-nav__link" href="/docs" data-act="goto-docs">Docs</a>
          <a class="home-nav__link" href="/roadmap" data-act="goto-roadmap">Roadmap</a>
        </nav>
      </div>
      <div class="home-header__right">
        ${headerActionButtons('home')}
        <button type="button" class="home-wallet" data-act="connect-phantom">Connect Wallet</button>
      </div>
    </header>`;
}

function HomeHero(): string {
  return `
    <section class="home-portal" aria-labelledby="home-title">
      <div class="home-portal__content">
        <p class="home-eyebrow">Browser Playtest</p>
        <h1 id="home-title">Gravelight</h1>
        <p class="home-portal__subtitle">Dark-fantasy tactical extraction roguelite.</p>
        <p class="home-portal__flavor">Leave the Waystation, descend for Gold, then extract before the lantern burns down.</p>
        <div class="home-actions" aria-label="Primary homepage actions">
          <button type="button" class="home-button home-button--primary home-button--play" data-act="play-from-home">Play</button>
          <button type="button" class="home-button home-button--secondary home-button--watch" data-act="goto-spectate">Spectate</button>
        </div>
        <p class="home-proof">Sign once. No gas. No transaction.</p>
        ${HomeStats()}
      </div>
    </section>`;
}

function HomeStats(): string {
  return `
    <dl class="home-stats" aria-label="Gravelight portal stats">
      <div>
        <dt>Online</dt>
        <dd><span aria-label="Online player count unavailable">${ONLINE_PLAYERS_STATUS}</span></dd>
      </div>
      <div>
        <dt>Players this month</dt>
        <dd>${esc(MONTHLY_PLAYERS_STATUS)}</dd>
      </div>
      <div>
        <dt>$OBOL</dt>
        <dd>${ContractPill()}</dd>
      </div>
    </dl>`;
}

function ContractPill(): string {
  if (!OBOL_CONTRACT_ADDRESS) {
    return '<span class="home-contract-status">—</span>';
  }

  return `
    <span class="home-contract">
      <code>${esc(OBOL_CONTRACT_ADDRESS)}</code>
      <button type="button" class="home-contract-copy" data-contract-copy data-copy-value="${esc(OBOL_CONTRACT_ADDRESS)}" aria-label="Copy OBOL contract address">Copy</button>
    </span>`;
}
